import React, { useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap'
import { setSelectedChatModel } from '../../../../_redux/actions/chatbotActions'
import { IconRefresh, IconChevronDown } from '@tabler/icons-react'
import { ChatbotContext } from '../context/ChatbotProvider'
import { filterChatModels } from '../utils/constants'

interface ChatModelDropdownInterface {
  type?: string
  onDropDownChange?: (selectedModel: any) => void
}

const ChatModelDropdown: React.FC<ChatModelDropdownInterface> = ({ type, onDropDownChange }) => {
  const dispatch = useDispatch()
  const { chatModels, selectedChatModel } = useSelector((state: any) => state.chatbotReducer)
  const { isStreaming } = useContext(ChatbotContext)
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false)
  const [showOtherModelsSubmenu, setShowOtherModelsSubmenu] = useState(false)
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 })
  const otherModelsTriggerRef = useRef<HTMLDivElement>(null)
  const isRegenerateType = type === 'regenerate' && onDropDownChange
  const [dropDirection, setDropDirection] = useState<'up' | 'down'>('down')
  const toggleWrapperRef = useRef<HTMLDivElement>(null)
  const filteredModels = filterChatModels(chatModels)
  const mainModels = filteredModels?.filter((model: any) => model.isMainModel) || []
  const otherModels = filteredModels?.filter((model: any) => !model.isMainModel) || []
  // Close submenu when clicking outside
  useEffect(() => {
    const handleResize = () => {
      if (showOtherModelsSubmenu) {
        handleOtherModelsHover(true)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [showOtherModelsSubmenu])

  const OtherModelsSubmenu = () => {
    const otherModels = filteredModels?.filter((model: any) => !model.isMainModel) || []

    if (!showOtherModelsSubmenu || otherModels.length === 0) return null

    return createPortal(
      <div
        className="fixed theme-surface border theme-border-default rounded-[1rem] shadow-lg w-[200px] max-h-[300px] overflow-y-auto z-[9999]"
        style={{
          top: `${submenuPosition.top}px`,
          left: `${submenuPosition.left}px`
        }}
        onMouseEnter={() => setShowOtherModelsSubmenu(true)}
        onMouseLeave={() => setShowOtherModelsSubmenu(false)}>
        {otherModels.map((model: any, index: number) => (
          <div
            key={index}
            onMouseDown={(e) => {
              e.stopPropagation()
              e.preventDefault()
              setShowOtherModelsSubmenu(false)
              setIsModelDropdownOpen(false)
              isRegenerateType ? onDropDownChange(model) : dispatch(setSelectedChatModel(model))
            }}
            className={`zoom-responsive flex flex-col gap-[5px] px-1 py-50 cursor-pointer hover:theme-text-heading theme-hover-surface transition-colors duration-150 ${
              selectedChatModel?.id === model?.id
                ? 'bg-[var(--color-primary-10)] text-[var(--color-primary)] border-l-4 border-[var(--color-primary)]'
                : ''
            }`}>
            <span
              className=" max-w-[220px] overflow-hidden whitespace-nowrap text-ellipsis font-medium"
              title={model?.displayName}>
              {model?.displayName}
            </span>
            {model?.description && (
              <span
                className="text-xs theme-text-subheading leading-tight max-w-[200px] overflow-hidden text-ellipsis"
                title={model.description}>
                {model.description}
              </span>
            )}
          </div>
        ))}
      </div>,
      document.body
    )
  }

  const handleOtherModelsHover = (show: boolean, event?: React.MouseEvent) => {
    if (show && otherModelsTriggerRef.current) {
      const rect = otherModelsTriggerRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const submenuWidth = 200
      const submenuHeight = 200 // Approximate max height
      const gap = 0

      // Calculate available space in all directions
      const spaceRight = viewportWidth - rect.right
      const spaceLeft = rect.left
      const spaceTop = rect.top
      const spaceBottom = viewportHeight - rect.bottom

      let left = rect.right + gap
      let top = rect.top

      // Determine horizontal position (left or right)
      if (spaceRight >= submenuWidth + gap) {
        // Position to the right (default)
        left = rect.right + gap
      } else if (spaceLeft >= submenuWidth + gap) {
        // Position to the left
        left = rect.left - submenuWidth - gap
      } else {
        // Not enough space on either side, position inside viewport
        if (spaceRight > spaceLeft) {
          left = viewportWidth - submenuWidth - gap
        } else {
          left = gap
        }
      }

      // Determine vertical position (top or bottom alignment)
      if (spaceBottom >= submenuHeight) {
        // Align with trigger top (default)
        top = rect.top
      } else if (spaceTop >= submenuHeight) {
        // Position above the trigger
        top = rect.bottom - submenuHeight
      } else {
        // Center vertically in viewport if no space above or below
        const centerY = viewportHeight / 2
        top = Math.max(
          gap,
          Math.min(centerY - submenuHeight / 2, viewportHeight - submenuHeight - gap)
        )
      }

      // Ensure submenu doesn't go outside viewport bounds
      left = Math.max(gap, Math.min(left, viewportWidth - submenuWidth - gap))
      top = Math.max(gap, Math.min(top, viewportHeight - submenuHeight - gap))

      setSubmenuPosition({
        top,
        left
      })
    }
    setShowOtherModelsSubmenu(show)
  }

  return (
    <>
      <div ref={toggleWrapperRef}>
        <UncontrolledDropdown
          direction={dropDirection}
          onToggle={() => {
            if (isStreaming) return
            if (!isModelDropdownOpen && toggleWrapperRef.current) {
              const rect = toggleWrapperRef.current.getBoundingClientRect()
              const spaceBelow = window.innerHeight - rect.bottom
              setDropDirection(spaceBelow < 320 ? 'up' : 'down')
            }
            setIsModelDropdownOpen(!isModelDropdownOpen)
          }}
          isOpen={isModelDropdownOpen}>
          <DropdownToggle
            tag="div"
            disabled={isStreaming}
            className={`${
              isRegenerateType
                ? 'rounded-[0.4rem] p-50'
                : 'p-50 ml-1 h-10 gap-[5px] rounded-[0.5rem]'
            }  flex justify-center items-center ${
              isStreaming ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
            } chat-primary theme-text-subheading ${
              isModelDropdownOpen ? 'theme-surface-muted' : ''
            } ${isRegenerateType && !isStreaming ? 'theme-hover-surface' : ''}`}
            data-testid={isRegenerateType ? 'regenerate-dropdown' : 'model-dropdown'}
            data-tooltip-id={isRegenerateType ? 'regenerate-dropdown' : 'model-dropdown'}>
            {isRegenerateType ? (
              <IconRefresh
                size={20}
                cursor={isStreaming ? 'not-allowed' : 'pointer'}
                color={'var(--gray-400)'}
              />
            ) : (
              selectedChatModel?.displayName || selectedChatModel || 'Select Model'
            )}
            <IconChevronDown
              size={18}
              color={
                isModelDropdownOpen
                  ? 'var(--color-primary)'
                  : isRegenerateType
                  ? 'var(--gray-400)'
                  : 'var(--gray-600)'
              }
            />
          </DropdownToggle>
          <DropdownMenu
            className="w-[200px] theme-surface border theme-border-default shadow-lg rounded-lg py-0"
            container="body"
            strategy="fixed">
            <div className="max-h-[300px] overflow-y-auto">
              {/* Main Models */}
              {mainModels?.map((model: any, index: number) => (
                <DropdownItem
                  key={index}
                  tag={'div'}
                  onClick={() => {
                    isRegenerateType
                      ? onDropDownChange(model)
                      : dispatch(setSelectedChatModel(model))
                  }}
                  className={`!flex flex-col cursor-pointer gap-[2px] transition-colors duration-150
                        ${
                          selectedChatModel?.id === model?.id
                            ? 'theme-active border-l-4 border-left-primary text-[var(--color-primary)]'
                            : 'theme-inactive  theme-text-subheading '
                        }
                        theme-hover-surface
                        focus:outline-none`}>
                  <span
                    className="max-w-[200px] overflow-hidden whitespace-nowrap text-ellipsis font-medium"
                    title={model?.displayName}>
                    {model?.displayName}
                  </span>
                  {model?.description && (
                    <span
                      className="text-xs theme-text-subheading leading-tight max-w-[200px] overflow-hidden text-wrap"
                      title={model.description}>
                      {model.description}
                    </span>
                  )}
                </DropdownItem>
              ))}

              {/* Other Models Trigger */}
              {otherModels?.length > 0 && (
                <div
                  ref={otherModelsTriggerRef}
                  onMouseEnter={() => handleOtherModelsHover(true)}
                  onMouseLeave={() => handleOtherModelsHover(false)}
                  className={`flex items-center justify-between cursor-pointer theme-hover-surface px-1 py-1 border-t theme-border-default transition-colors duration-150`}>
                  <span className="theme-text-subheading">Other models</span>
                  <IconChevronDown size={16} className="theme-icon-muted rotate-90-inverse" />
                </div>
              )}
            </div>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
      <OtherModelsSubmenu />
    </>
  )
}

export default ChatModelDropdown
